import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8080';

function CustomerField({ value, onSelect }) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState(''); // ──▶ separate name field
  const [wantsSMS, setWantsSMS] = useState(false);
  const [wantsEmail, setWantsEmail] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const containerRef = useRef(null);
  const ignoreNextSearch = useRef(false);
  const hasAutoExtractedPhone = useRef(false);

  // Sync with parent value
  useEffect(() => {
    if (!value) {
      setInput('');
    } else {
      setInput(value.name);
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOptions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset modal fields when opened
  useEffect(() => {
    if (showModal) {
      setPhoneError('');
      setNameError('');
      setNewEmail('');
      setWantsSMS(false);
      setWantsEmail(false);
    }
  }, [showModal]);

  // Search customers
  useEffect(() => {
    if (ignoreNextSearch.current) {
      ignoreNextSearch.current = false;
      return;
    }

    const trimmed = input.trim();
    if (trimmed.length < 2) {
      setOptions([]);
      return;
    }

    const controller = new AbortController();

    fetch(`${API}/customers/search?q=${trimmed}`, {
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        const digits = trimmed.replace(/\D/g, '');
        const hasValidPhone = digits.length === 10;

        if (data.length === 0 && hasValidPhone && !hasAutoExtractedPhone.current) {
          setNewPhone(digits);
          setNewName('');  // ──▶ empty name for user to fill
          setInput('');
          setShowModal(true);
          hasAutoExtractedPhone.current = true;
          return;
        }

        const filteredData = data.filter(c => {
          if (!hasValidPhone) return true;
          return c.phone === digits;
        });

        setOptions(filteredData);
        setShowModal(filteredData.length === 0);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err);
      });

    return () => controller.abort();
  }, [input]);

  const handleSelect = (cust) => {
    ignoreNextSearch.current = true;
    setInput(cust.name);
    setOptions([]);
    setShowModal(false);
    onSelect(cust);
    setNewPhone('');
    setNewEmail('');
    setNewName('');
  };

  const isValidEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const handleNewCustomer = async () => {
    // Validate name
    if (!newName.trim()) {
      setNameError("Το όνομα πελάτη είναι υποχρεωτικό.");
      toast.error("Το όνομα πελάτη δεν μπορεί να είναι κενό.");
      return;
    } else {
      setNameError('');
    }

    // Validate phone
    if (!/^69\d{8}$/.test(newPhone)) {
      setPhoneError("Το κινητό πρέπει να ξεκινά με 69 και να έχει 10 ψηφία.");
      toast.error("Το κινητό πρέπει να ξεκινά με 69 και να έχει 10 ψηφία.");
      return;
    } else {
      setPhoneError('');
    }

    // Validate email if provided
    if (newEmail.trim() && !isValidEmail(newEmail)) {
      toast.error("Το email δεν είναι έγκυρο.");
      return;
    }

    try {
      // Check if phone exists
      const checkRes = await fetch(`${API}/customers/check-phone?phone=${newPhone}`);
      const checkData = await checkRes.json();

      if (checkData.exists) {
        toast.error("Το κινητό υπάρχει ήδη.");
        return;
      }

      // Add new customer
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name:  newName,
          phone: newPhone,
          email: newEmail || null,
        }),
      });

      const data = await res.json();

      if (data.id) {
        toast.success("Ο πελάτης καταχωρήθηκε!");
        handleSelect({
          id:    data.id,
          name:  data.name,
          phone: data.phone,
          email: data.email,
        });
        setShowModal(false);
      } else {
        toast.error("Σφάλμα κατά την καταχώριση πελάτη");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Σφάλμα επικοινωνίας με τον server");
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        placeholder="Αναζήτηση πελάτη..."
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          onSelect(null);
        }}
        required
        className={`w-full border rounded p-2 bg-gray-100 text-gray-600 ${
          nameError ? 'border-red-500' : 'border-gray-300'
        }`}
        onFocus={() => {
          if (!value) setShowModal(false);
        }}
      />

      {nameError && (
        <div className="text-red-600 text-sm mt-1">{nameError}</div>
      )}

      {/* Clear button */}
      {value && (
        <button
          type="button"
          onClick={() => {
            onSelect(null);
            setInput('');
            setOptions([]);
            hasAutoExtractedPhone.current = false;
            setWantsSMS(false);
            setWantsEmail(false);
          }}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 z-10"
        >
          ×
        </button>
      )}

      {/* Dropdown */}
      {options.length > 0 && !value && (
        <ul className="absolute left-0 mt-1 border border-gray-300 bg-white rounded shadow z-20 w-[max(100%,30rem)]">
          {options.map((cust) => (
            <li
              key={cust.id}
              onMouseDown={() => handleSelect(cust)}
              className="cursor-pointer px-3 py-1 hover:bg-gray-100 border-b last:border-none"
            >
              {cust.name} – {cust.phone}
            </li>
          ))}
        </ul>
      )}

      {/* New customer modal */}
      {showModal && !value && (
        <div className="absolute left-0 mt-2 w-full max-w-5xl border border-gray-300 bg-gray-50 p-4 rounded-lg shadow z-20 sm:w-[95vw] lg:w-[calc(100vw-5rem)]">
          <h4 className="font-semibold mb-2">➕ Νέος Πελάτης</h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">

            {/* Name field — now editable */}
            <div className="w-full">
              <input
                type="text"
                placeholder="Ονοματεπώνυμο"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={`w-full border rounded p-2 ${
                  nameError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {nameError && (
                <div className="text-red-600 text-sm mt-1">{nameError}</div>
              )}
            </div>

            {/* Phone field */}
            <div className="w-full">
              <input
                type="text"
                placeholder="Κινητό"
                value={newPhone}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '');
                  if (raw.length <= 10) setNewPhone(raw);
                }}
                required
                className={`w-full rounded p-2 mb-1 border ${
                  phoneError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {phoneError && (
                <div className="text-red-600 text-sm mt-1">{phoneError}</div>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <span>Αποστολή SMS</span>
                <div className="relative w-10 h-5">
                  <input
                    type="checkbox"
                    checked={wantsSMS}
                    onChange={() => setWantsSMS(!wantsSMS)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors"></div>
                  <div className="absolute top-0 left-0 w-5 h-5 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5"></div>
                </div>
              </label>
            </div>

            {/* Email field */}
            <div className="w-full">
              <input
                type="email"
                placeholder="Email (προαιρετικό)"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full border border-gray-300 rounded p-2 mb-2"
              />
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <span>Αποστολή Email</span>
                <div className="relative w-10 h-5">
                  <input
                    type="checkbox"
                    checked={wantsEmail}
                    onChange={() => {
                      if (!newEmail.trim()) {
                        toast.error("Δεν έχει εισαχθεί email.");
                        return;
                      }
                      setWantsEmail(!wantsEmail);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-indigo-500 transition-colors"></div>
                  <div className="absolute top-0 left-0 w-5 h-5 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5"></div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleNewCustomer}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
            >
              Καταχώριση
            </button>
            <button
              onClick={() => {
                setShowModal(false);
                setInput('');
                setNewPhone('');
                setNewEmail('');
                setNewName('');
                hasAutoExtractedPhone.current = false;
                setPhoneError('');
                setNameError('');
                setOptions([]);
              }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded"
            >
              Άκυρο
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerField;